import { Injectable, NgZone, computed, signal } from '@angular/core';
import type { HubConnection } from '@microsoft/signalr';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { SessionService } from './session.service';

export type TranslationStatusNotificationState =
    | 'started'
    | 'updated'
    | 'completed'
    | 'error';

export interface TranslationStatusNotification {
    id: string;
    submissionId: string | null;
    title: string;
    message: string;
    state: TranslationStatusNotificationState;
    updatedAt: Date;
}

@Injectable({
    providedIn: 'root'
})
export class TranslationStatusService {
    private readonly hubUrl = this.buildApiUrl('codeTranslation/status');
    private readonly notificationsSignal = signal<TranslationStatusNotification[]>([]);
    private readonly trackedSubmissionIds = new Set<string>();
    private readonly friendlyNames = new Map<string, string>();

    private signalRModule: typeof import('@microsoft/signalr') | null = null;
    private signalRModulePromise: Promise<typeof import('@microsoft/signalr')> | null = null;
    private connection: HubConnection | null = null;
    private startPromise: Promise<void> | null = null;
    private pendingStatusChecks = 0;

    readonly notifications = this.notificationsSignal.asReadonly();
    readonly hasNotifications = computed(() => this.notificationsSignal().length > 0);

    constructor(
        private readonly sessionService: SessionService,
        private readonly zone: NgZone
    ) { }

    async trackSubmission(submissionId: string | null | undefined, friendlyName?: string | null): Promise<void> {
        const normalizedSubmissionId = this.firstText(submissionId);
        if (!normalizedSubmissionId) return;

        const normalizedFriendlyName = this.firstText(friendlyName);
        if (normalizedFriendlyName) {
            this.friendlyNames.set(normalizedSubmissionId, normalizedFriendlyName);
        }

        this.trackedSubmissionIds.add(normalizedSubmissionId);
        this.upsertNotification('started', normalizedSubmissionId, {
            statusMessage: 'Checking translation status.'
        });

        if (environment.useMockTranslations) {
            this.simulateMockStatusUpdates(normalizedSubmissionId);
            return;
        }

        let statusCheckStarted = false;

        try {
            const connection = await this.getConnection();
            await this.startConnection(connection);
            this.pendingStatusChecks++;
            statusCheckStarted = true;
            await connection.invoke('CheckTranslationStatus', normalizedSubmissionId);
        } catch (error) {
            this.zone.run(() => {
                if (this.shouldIgnoreInvocationCancellation(error, normalizedSubmissionId)) return;

                this.handleStatusError(error, normalizedSubmissionId);
            });
        } finally {
            if (statusCheckStarted) {
                this.pendingStatusChecks = Math.max(0, this.pendingStatusChecks - 1);
                this.zone.run(() => this.maybeStopConnection());
            }
        }
    }

    dismiss(notificationId: string): void {
        this.notificationsSignal.update((notifications) =>
            notifications.filter((notification) => notification.id !== notificationId)
        );
    }

    private async getConnection(): Promise<HubConnection> {
        if (this.connection) return this.connection;

        const signalR = await this.getSignalRModule();
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(this.hubUrl, {
                accessTokenFactory: async () => firstValueFrom(this.sessionService.getAccessToken())
            })
            .withAutomaticReconnect()
            .build();

        connection.on('TranslationStatusStarted', (status: unknown) => {
            this.zone.run(() => this.handleStatus('started', status));
        });

        connection.on('TranslationStatusUpdated', (status: unknown) => {
            this.zone.run(() => this.handleStatus('updated', status));
        });

        connection.on('TranslationStatusCompleted', (status: unknown) => {
            this.zone.run(() => this.handleStatus('completed', status));
        });

        connection.on('TranslationStatusError', (error: unknown) => {
            this.zone.run(() => this.handleStatusError(error));
        });

        connection.onclose((error) => {
            if (!error || this.trackedSubmissionIds.size === 0) return;

            this.zone.run(() => {
                this.addConnectionNotification(
                    'Connection closed before all translation status updates were received.'
                );
            });
        });

        this.connection = connection;
        return connection;
    }

    private async startConnection(connection: HubConnection): Promise<void> {
        const signalR = await this.getSignalRModule();
        if (connection.state === signalR.HubConnectionState.Connected) return;
        if (this.startPromise) return this.startPromise;

        this.startPromise = connection.start()
            .finally(() => {
                this.startPromise = null;
            });

        return this.startPromise;
    }

    private handleStatus(state: TranslationStatusNotificationState, status: unknown): void {
        const submissionId = this.getSubmissionId(status) ?? this.getOnlyTrackedSubmissionId();

        if (!submissionId) {
            this.addConnectionNotification(this.getStatusMessage(status, state), state);
            return;
        }

        this.upsertNotification(state, submissionId, status);

        if (state === 'completed') {
            this.trackedSubmissionIds.delete(submissionId);
            this.maybeStopConnection();
        }
    }

    private handleStatusError(error: unknown, fallbackSubmissionId: string | null = null): void {
        const submissionId = this.getSubmissionId(error) ?? fallbackSubmissionId ?? this.getOnlyTrackedSubmissionId();

        if (!submissionId) {
            this.addConnectionNotification(this.getStatusMessage(error, 'error'), 'error');
            return;
        }

        this.upsertNotification('error', submissionId, error);
        this.trackedSubmissionIds.delete(submissionId);
        this.maybeStopConnection();
    }

    private upsertNotification(
        state: TranslationStatusNotificationState,
        submissionId: string,
        status: unknown
    ): void {
        const title = this.getNotificationTitle(state, submissionId);
        const message = this.getStatusMessage(status, state);

        this.notificationsSignal.update((notifications) => {
            const existingIndex = notifications.findIndex(
                (notification) => notification.submissionId === submissionId
            );
            const nextNotification: TranslationStatusNotification = {
                id: existingIndex >= 0 ? notifications[existingIndex].id : this.createNotificationId(),
                submissionId,
                title,
                message,
                state,
                updatedAt: new Date()
            };

            if (existingIndex < 0) {
                return [nextNotification, ...notifications];
            }

            const nextNotifications = [...notifications];
            nextNotifications.splice(existingIndex, 1);
            return [nextNotification, ...nextNotifications];
        });
    }

    private addConnectionNotification(
        message: string,
        state: TranslationStatusNotificationState = 'error'
    ): void {
        const notification: TranslationStatusNotification = {
            id: this.createNotificationId(),
            submissionId: null,
            title: state === 'error' ? 'Translation status error' : 'Translation status update',
            message,
            state,
            updatedAt: new Date()
        };

        this.notificationsSignal.update((notifications) => [notification, ...notifications]);
    }

    private getNotificationTitle(
        state: TranslationStatusNotificationState,
        submissionId: string
    ): string {
        const friendlyName = this.friendlyNames.get(submissionId);
        const translationName = friendlyName ? friendlyName : 'Translation';

        if (state === 'started') return `${translationName} status started`;
        if (state === 'completed') return `${translationName} completed`;
        if (state === 'error') return `${translationName} status error`;

        return `${translationName} status updated`;
    }

    private getStatusMessage(status: unknown, state: TranslationStatusNotificationState): string {
        const statusMessage = this.firstText(
            this.getStringProperty(status, 'statusMessage', 'StatusMessage'),
            this.getStringProperty(status, 'message', 'Message'),
            this.getStringProperty(status, 'status', 'Status'),
            typeof status === 'string' ? status : null
        );

        if (statusMessage) return statusMessage;
        if (status instanceof Error && this.firstText(status.message)) return status.message.trim();

        if (state === 'started') return 'Checking translation status.';
        if (state === 'completed') return 'Translation completed.';
        if (state === 'error') return 'Unable to check translation status.';

        return 'Translation status updated.';
    }

    private getSubmissionId(status: unknown): string | null {
        return this.firstText(
            this.getStringProperty(status, 'submissionId', 'SubmissionId'),
            this.getStringProperty(status, 'id', 'Id')
        );
    }

    private getOnlyTrackedSubmissionId(): string | null {
        if (this.trackedSubmissionIds.size !== 1) return null;

        const [submissionId] = this.trackedSubmissionIds;
        return submissionId ?? null;
    }

    private maybeStopConnection(): void {
        const signalR = this.signalRModule;

        if (
            this.trackedSubmissionIds.size > 0 ||
            this.pendingStatusChecks > 0 ||
            !this.connection ||
            !signalR ||
            this.connection.state === signalR.HubConnectionState.Disconnected
        ) {
            return;
        }

        void this.connection.stop();
    }

    private shouldIgnoreInvocationCancellation(error: unknown, submissionId: string): boolean {
        return (
            !this.trackedSubmissionIds.has(submissionId) &&
            this.hasTerminalNotification(submissionId) &&
            this.getErrorMessage(error).includes('Invocation canceled due to the underlying connection being closed')
        );
    }

    private hasTerminalNotification(submissionId: string): boolean {
        return this.notificationsSignal().some((notification) =>
            notification.submissionId === submissionId &&
            (notification.state === 'completed' || notification.state === 'error')
        );
    }

    private async getSignalRModule(): Promise<typeof import('@microsoft/signalr')> {
        if (this.signalRModule) return this.signalRModule;
        if (!this.signalRModulePromise) {
            this.signalRModulePromise = import('@microsoft/signalr')
                .catch((error) => {
                    this.signalRModulePromise = null;
                    throw error;
                });
        }

        this.signalRModule = await this.signalRModulePromise;
        return this.signalRModule;
    }

    private simulateMockStatusUpdates(submissionId: string): void {
        window.setTimeout(() => {
            this.handleStatus('updated', {
                submissionId,
                statusMessage: 'Translation is in progress.'
            });
        }, 800);

        window.setTimeout(() => {
            this.handleStatus('completed', {
                submissionId,
                statusMessage: 'Translation completed.'
            });
        }, 1800);
    }

    private getStringProperty(source: unknown, ...keys: string[]): string | null {
        if (!this.isRecord(source)) return null;

        for (const key of keys) {
            const value = source[key];
            if (typeof value === 'string' && value.trim().length > 0) return value.trim();
        }

        return null;
    }

    private getErrorMessage(error: unknown): string {
        if (error instanceof Error) return error.message;
        if (typeof error === 'string') return error;

        return this.getStringProperty(error, 'message', 'Message') ?? '';
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private firstText(...values: Array<string | null | undefined>): string | null {
        for (const value of values) {
            if (typeof value === 'string' && value.trim().length > 0) return value.trim();
        }

        return null;
    }

    private createNotificationId(): string {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return crypto.randomUUID();
        }

        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    private buildApiUrl(path: string): string {
        const baseUrl = environment.apiBaseUrl.replace(/\/+$/, '');
        return baseUrl ? `${baseUrl}/${path}` : `/${path}`;
    }
}
